import { useMemo } from "react";

/**
 * Hook to determine message type based on message content
 * @param {string} message - The message to analyze
 * @returns {string} - The message type: 'success', 'error', 'warning', or 'info'
 */
export const useMessageType = (message) => {
  return useMemo(() => {
    if (!message || typeof message !== "string") {
      return "info";
    }

    const messageLower = message.toLowerCase();

    // Success indicators
    const successKeywords = [
      "success",
      "successfully",
      "saved",
      "created",
      "updated",
      "deleted",
      "sent",
      "completed",
      "done",
      "added",
      "removed",
    ];

    // Error indicators
    const errorKeywords = [
      "error",
      "failed",
      "failure",
      "invalid",
      "unauthorized",
      "forbidden",
      "not found",
      "session expired",
      "please login",
      "try again",
      "something went wrong",
    ];

    // Warning indicators
    const warningKeywords = [
      "warning",
      "caution",
      "attention",
      "notice",
      "please note",
      "important",
    ];

    // Check for success
    if (successKeywords.some((keyword) => messageLower.includes(keyword))) {
      // Make sure it's not an error message with success keywords
      if (!errorKeywords.some((keyword) => messageLower.includes(keyword))) {
        return "success";
      }
    }

    // Check for error
    if (errorKeywords.some((keyword) => messageLower.includes(keyword))) {
      return "error";
    }

    // Check for warning
    if (warningKeywords.some((keyword) => messageLower.includes(keyword))) {
      return "warning";
    }

    // Default to info
    return "info";
  }, [message]);
};

export default useMessageType;
